<template>

<div class="end">

	<app-topbar />

	<main class="end-body">

		<div class="end-shell">

			<!-- LEFT column: the full box art as one image (the puzzle is over,
			     so it's safe — keyed by the answer game id the client now knows).
			     Natural aspect, uncropped. -->
			<div class="end-art-col">
				<div class="end-art-wrap">
					<div class="end-art" :style="artStyle">
						<img v-if="fullUrl" class="end-art-img" :src="fullUrl" alt="" />
						<div v-else class="end-art-placeholder">BOX ART</div>
					</div>
				</div>

				<!-- archive: step through past days without leaving the reveal
				     (shown for both a practice replay and a real scored result
				     opened from the archive — prevDate/nextDate are only set in
				     that archive context) -->
				<div v-if="prevDate || nextDate" class="end-practice-nav">
					<button v-if="prevDate" type="button" class="end-practice-nav-btn" v-on:click="$emit('navigate', prevDate)">‹ Previous</button>
					<span v-else class="end-practice-nav-gap"></span>
					<button v-if="nextDate" type="button" class="end-practice-nav-btn" v-on:click="$emit('navigate', nextDate)">Next ›</button>
				</div>

				<!-- desktop: the countdown sits under the box art (hidden on mobile,
				     where the copy below the main column carries it instead).
				     Not shown for a practice replay of a past day. -->
				<div v-if="!archive" class="end-next end-next--art">
					<span class="end-next-label">Next box art in</span>
					<span class="end-next-time">{{ countdown }}</span>
				</div>
			</div>

			<!-- RIGHT column: title, outcome, stats, share, countdown -->
			<div class="end-main-col">

				<!-- title block: outcome eyebrow + game name + meta line -->
				<div class="end-title">
					<div class="end-eyebrow" :class="isPreview ? 'is-preview' : (solved ? 'is-solved' : 'is-failed')">
						{{ isPreview ? 'Curator preview' : (solved ? 'Got it' : 'Out of guesses') }}
					</div>
					<h1 class="end-name">{{ answerName }}</h1>
					<div v-if="answerMeta" class="end-meta">{{ answerMeta }}</div>
				</div>

				<!-- the score panel shows for live, practice AND curator preview.
				     A practice replay renders the SAME box, but greyed and tagged
				     PRACTICE so it reads as unranked. -->
				<div class="end-score-block">
					<div class="end-section-head">
						Your score
						<span v-if="practice" class="end-practice-tag">Practice</span>
					</div>
					<div class="end-score" :class="practice ? 'is-practice' : (solved ? 'is-solved' : 'is-failed')">
						<div class="end-score-value">{{ score != null ? score : 0 }}</div>
						<div class="end-score-detail">
							<div class="end-score-head">{{ outcomeLine }}</div>
							<div class="end-score-sub">{{ tilesLine }}</div>
							<p v-if="practice" class="end-practice-note">Practice replay, not ranked. Doesn't affect your streak.</p>
						</div>
					</div>
				</div>

				<!-- a signed-in player viewing a finished LIVE result that lives only
				     in this browser (played as a guest, not yet synced): a quiet
				     nudge to save it to the account, opening the sync dialog -->
				<div v-if="showUnsynced" class="end-unsynced">
					<span class="end-unsynced-text">Not synced to your account</span>
					<button type="button" class="end-unsynced-btn" v-on:click="syncNow">Sync</button>
				</div>

				<!-- stats: the global distribution shows for every run (incl. a
				     practice replay of a past day); the personal tiles + nudge are
				     suppressed in practice (unranked, no streak meaning) -->
				<end-stats
					:practice="practice"
					:archive="archive"
					:preview="isPreview"
					:self-guesses="guesses"
					:self-solved="solved"
				/>

				<!-- share controls (curator preview shows it too, with fake data) -->
				<div class="end-share">
					<div class="end-share-head">Share your result</div>
					<end-share :result="enrichedResult" :number="number" />
				</div>

				<!-- daily-reminder opt-in: live result + curator preview (self-gates
				     on signed-in + not-already-set + not-dismissed) -->
				<end-reminder v-if="!practice && !archive" />

				<!-- next puzzle countdown (mobile; on desktop it moves under the art).
				     Not shown for a practice replay of a past day. -->
				<div v-if="!archive" class="end-next end-next--main">
					<span class="end-next-label">Next box art in</span>
					<span class="end-next-time">{{ countdown }}</span>
				</div>

			</div>

		</div>

	</main>

	<app-foot />

</div>

</template>

<script>

/*
	The end / reveal screen, shown once game.result is set (a solved or lost run).

	Layout is editorial and two-column on desktop — the full box art on the left,
	the answer + outcome + stats + share on the right, the pair centred in the
	viewport — and a single stacked column on mobile.

	It reveals the FULL box art at its NATURAL aspect (uncropped): we point at the
	full-image endpoint keyed by the now-known answer id (`/api/boxart/full/{id}.jpg`),
	NOT the cropped canonical avatar. The art box is pre-sized to the box's known
	aspect ratio (descriptor `today.art = {w,h}`) so its height is reserved before
	the image loads — no content jump — and the image fills that box (object-fit
	contain).

	The store's game.result carries { solved, lost, answer, score } — the guess
	and tile counts live on getters (guessesUsed / tilesRevealed), so we enrich a
	local copy with `guesses` and `tiles` before handing it to <end-share>
	(share.js is a pure function of that enriched shape).

	Practice (a replay of a past date) is unranked: we show a "Practice — not
	ranked" banner and hide the score panel + stats / streak language.
*/

import api from '../service/api'
import bus from '../service/bus'
import Stats from '../components/end/Stats.vue'
import Share from '../components/end/Share.vue'
import Reminder from '../components/end/Reminder.vue'

// fabricated outcome for the CURATOR PREVIEW only, so an admin reviewing today
// sees the real score panel + personal stats layout populated with placeholder
// numbers (never recorded, never shown to a real player)
var PREVIEW_FAKE = { guesses: 4, tiles: 4 }

export default {

	name: 'End',

	components: {
		'end-stats': Stats,
		'end-share': Share,
		'end-reminder': Reminder
	},

	props: {
		// practice only: adjacent archived dates for the prev/next nav under the
		// art. Selecting one emits `navigate` with that date (Daily routes to it).
		prevDate: {
			default: null
		},
		nextDate: {
			default: null
		},
		// viewing a past day from the archive (regardless of practice vs a real
		// synced result): suppresses the live-only chrome (next-puzzle countdown,
		// the "Your stats" lifetime panel).
		archive: {
			type: Boolean,
			default: false
		}
	},

	data: function() {

		return {
			// "Next puzzle in HH:MM:SS", ticked every second
			countdown: '',
			countdownTimer: null
		}

	},

	computed: {

		result: function() {

			return this.$store.getters['game/result']

		},

		practice: function() {

			return this.$store.state.game.practice

		},

		signedIn: function() {

			return this.$store.getters['account/signedIn']

		},

		// a finished result (live OR practice) held only locally, not on the
		// account. Shown to signed-in players as a "not synced" nudge. onAccount
		// is set true for both a live result and a practice result that the
		// account already holds, so this only fires for genuinely local-only plays.
		showUnsynced: function() {

			return this.signedIn && this.$store.getters['game/unsynced']

		},

		date: function() {

			return this.$store.state.game.date

		},

		today: function() {

			return this.$store.getters['game/today']

		},

		number: function() {

			var today = this.today

			return (today && (today.number != null ? today.number : today.no)) || null

		},

		guesses: function() {

			// curator preview: fabricated placeholder so the score/stats render
			if (this.isPreview) return PREVIEW_FAKE.guesses

			return this.$store.getters['game/guessesUsed']

		},

		tiles: function() {

			if (this.isPreview) return PREVIEW_FAKE.tiles

			return this.$store.getters['game/tilesRevealed']

		},

		solved: function() {

			if (this.isPreview) return true

			return !!(this.result && this.result.solved)

		},

		// derive the score from tiles (the deterministic server formula) rather
		// than trusting result.score, which may be stale/missing (e.g. an old
		// localStorage result saved before the score was returned). Loss = 0.
		score: function() {

			if (!this.solved) return 0

			return Math.round(1000 * (17 - this.tiles) / 16)

		},

		// the curator-preview answer (admin viewing today without playing), else null
		previewAnswer: function() {

			return this.$store.getters['game/preview']

		},

		// true when this reveal is a curator preview (answer shown up front, no
		// recorded result) rather than a played-out finish
		isPreview: function() {

			return !!this.previewAnswer && !this.result

		},

		answer: function() {

			return (this.result && this.result.answer) || this.previewAnswer || null

		},

		answerName: function() {

			return (this.answer && this.answer.name) || 'Revealed'

		},

		// the whole box art for the reveal: one image, keyed by the now-known
		// answer game id (safe — the puzzle is resolved)
		fullUrl: function() {

			var id = this.answer && this.answer.id

			return id ? api.image('full/' + id + '.jpg') : ''

		},

		// pre-size the art box to the box's known aspect ratio (shipped in the
		// descriptor as today.art = {w,h}) so the container reserves its height
		// BEFORE the image loads — no content jump on load. Falls back to square
		// when dims are unknown (matches the placeholder).
		artStyle: function() {

			var today = this.$store.getters['game/today']
			var art = today && today.art

			if (art && art.w > 0 && art.h > 0) {

				return { aspectRatio: art.w + ' / ' + art.h }

			}

			return { aspectRatio: '1 / 1' }

		},

		// "2019 · Jordy Adan · BGG Rank #142" — year, designer(s), rank
		answerMeta: function() {

			var a = this.answer

			if (!a) return ''

			var parts = []

			if (a.year) parts.push(a.year)

			var designers = this.designerNames

			if (designers) parts.push(designers)

			if (a.rank) parts.push('BGG Rank #' + a.rank)

			return parts.join(' · ')

		},

		designerNames: function() {

			var designers = this.answer && this.answer.designers

			if (!designers || !designers.length) return ''

			return designers.map(function(d) {

				// designers may be plain strings or { name } objects
				return (typeof d === 'string') ? d : (d && d.name)

			}).filter(Boolean).join(', ')

		},

		outcomeLine: function() {

			if (this.solved) {

				return 'In ' + this.tiles + ' tile' + (this.tiles === 1 ? '' : 's')

			}

			return 'Out of guesses'

		},

		tilesLine: function() {

			return this.tiles + ' tile' + (this.tiles === 1 ? '' : 's') + ' shown · fewer is better'

		},

		// the result enriched with guess/tile counts for share.js
		enrichedResult: function() {

			var r = this.result || {}

			return {
				// preview-aware getters so the curator preview's fabricated grid +
				// score render too (this.solved/score/guesses/tiles handle preview)
				solved: this.solved,
				lost: !this.solved && !!r.lost,
				score: this.score,
				guesses: this.guesses,
				tiles: this.tiles,
				answer: r.answer || this.previewAnswer || null
			}

		}

	},

	created: function() {

		// rendered inline by Daily once a run finishes (a result is present) or as
		// a curator preview (answer up front, no result); guard against neither
		if (!this.result && !this.isPreview) return

		this.tick()
		this.countdownTimer = setInterval(this.tick, 1000)

	},

	beforeDestroy: function() {

		if (this.countdownTimer) clearInterval(this.countdownTimer)

	},

	methods: {

		// open the sync dialog so the player can save this (and any other) local
		// device play onto their account. Opens immediately (loading state) and
		// force so it's offered even if a prior sync flagged it.
		syncNow: function() {

			bus.$emit('sync-open', { force: true })

		},

		/*
			Time until the next puzzle. The day boundary is server-defined: the
			puzzle rolls over at ROLLOVER_HOUR in UK local time (Europe/London),
			matching Boxart\Puzzle::ROLLOVER_HOUR/ROLLOVER_TZ on the backend. We
			read the current London wall clock (via Intl, so BST/GMT is handled
			automatically) and count down to the next 10:00 London. Working from
			the wall-clock time-of-day makes this correct regardless of the
			viewer's own timezone.
		*/
		tick: function() {

			var ROLLOVER_HOUR = 10

			// current wall-clock time in London (handles GMT/BST automatically)
			var parts = new Intl.DateTimeFormat('en-GB', {
				timeZone: 'Europe/London',
				hour12: false,
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit'
			}).formatToParts(new Date())

			var get = function(type) {
				var p = parts.find(function(x) { return x.type === type })
				return p ? parseInt(p.value, 10) : 0
			}

			var h = get('hour')
			// some engines render midnight as 24 under hour12:false — normalise
			if (h === 24) h = 0

			var nowSec = h * 3600 + get('minute') * 60 + get('second')
			var target = ROLLOVER_HOUR * 3600

			// seconds until the next 10:00 London (wrap to tomorrow if past)
			var diff = target - nowSec
			if (diff <= 0) diff += 86400

			var hh = Math.floor(diff / 3600)
			var mm = Math.floor((diff % 3600) / 60)
			var ss = diff % 60

			this.countdown = this.pad(hh) + ':' + this.pad(mm) + ':' + this.pad(ss)

		},

		pad: function(n) {

			return (n < 10 ? '0' : '') + n

		}

	}

}

</script>

<style scoped>

.end {
	display: flex;
	flex-direction: column;
	min-height: 100%;
	flex: 1;
}

/* the reveal sits centred in the available height on large screens */
.end-body {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 40px 10px;
}

/* mobile-first: single stacked column, art over content */
.end-shell {
	width: 100%;
	max-width: 440px;
	display: flex;
	flex-direction: column;
	gap: 22px;
}

.end-art-col {
	width: 100%;
}

.end-main-col {
	display: flex;
	flex-direction: column;
	gap: 18px;
}

/* full box art in the editorial frame — natural aspect, never cropped */
.end-art-wrap {
	padding: 14px;
	background: #fbf8f1;
	border: 1px solid var(--hairline);
	border-radius: 8px;
	box-shadow: 0 14px 40px rgba(40, 34, 24, 0.08);
}

.end-art {
	position: relative;
	width: 100%;
	border-radius: var(--radius-sm);
	overflow: hidden;
	background: #b9794f;
	display: flex;
	align-items: center;
	justify-content: center;
}

/* fill the pre-sized (aspect-ratio) box so the image never resizes the
   container as it loads — the box reserves its height up front */
.end-art-img {
	position: absolute;
	inset: 0;
	display: block;
	width: 100%;
	height: 100%;
	object-fit: contain;
	animation: end-fade 0.8s ease both;
}

@keyframes end-fade {
	from { opacity: 0; }
	to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {

	.end-art-img {
		animation: none;
	}

}

/* placeholder fills the pre-sized box (the box now owns the aspect ratio) */
.end-art-placeholder {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	color: rgba(255, 255, 255, 0.7);
	font-family: var(--font-mono);
	font-size: 11px;
	letter-spacing: 0.22em;
}

/* title block */
.end-title {
	text-align: center;
}

.end-eyebrow {
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.2em;
	text-transform: uppercase;
}

.end-eyebrow.is-solved {
	color: var(--correct);
}

.end-eyebrow.is-failed {
	color: var(--wrong);
}

.end-eyebrow.is-preview {
	color: var(--muted-2);
}

.end-name {
	font-family: var(--font-display);
	font-size: 28px;
	font-weight: 600;
	letter-spacing: -0.01em;
	margin: 6px 0 0;
	color: var(--ink);
	line-height: 1.08;
}

.end-meta {
	font-family: var(--font-body);
	font-size: 13px;
	color: var(--muted);
	margin-top: 6px;
}

/* practice variant of the score box: a PRACTICE tag in the head, a greyed box,
   and an unranked note beneath, so an archived practice replay reads clearly as
   practice while still showing the score */
.end-practice-tag {
	margin-left: 8px;
	padding: 2px 7px;
	border: 1px solid var(--tile);
	border-radius: 4px;
	font-size: 9px;
	letter-spacing: 0.1em;
	color: var(--muted-2);
	vertical-align: middle;
}

.end-practice-note {
	margin: 6px 0 0;
	font-family: var(--font-body);
	font-size: 11px;
	line-height: 1.35;
	color: var(--muted-2);
}

/* "not synced to your account" nudge for a local-only finished result */
.end-unsynced {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 11px 14px;
	border: 1px solid #e7dcc4;
	border-radius: var(--radius);
	background: #fbf4e3;
}

.end-unsynced-text {
	font-family: var(--font-body);
	font-size: 13px;
	color: var(--muted-2);
}

.end-unsynced-btn {
	flex: none;
	height: 32px;
	padding: 0 16px;
	border: none;
	border-radius: 8px;
	background: var(--ink);
	color: var(--paper);
	font-family: var(--font-body);
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
}

.end-unsynced-btn:hover {
	background: var(--accent);
}

/* score panel */
/* a section head, matching the Stats heads ("Your stats" / "Tiles to solve") */
.end-section-head {
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.14em;
	color: var(--muted);
	text-transform: uppercase;
	margin-bottom: 12px;
}

/* phones: centre the head, matching the centred Stats heads */
@media (max-width: 599px) {

	.end-section-head {
		text-align: center;
	}

}

.end-score {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 14px;
	padding: 16px 18px;
	border: 1px solid var(--correct-tint);
	border-radius: 10px;
	background: #eef1ea;
}

.end-score.is-failed {
	background: var(--wrong-tint);
	border-color: #f0e3df;
}

.end-score.is-practice {
	background: #f1eee8;
	border-color: var(--hairline);
}

.end-score-value {
	font-family: var(--font-mono);
	font-size: 42px;
	font-weight: 500;
	line-height: 1;
	color: var(--correct);
}

.end-score.is-failed .end-score-value {
	color: var(--wrong);
}

.end-score.is-practice .end-score-value {
	color: var(--muted-2);
}

.end-score-detail {
	text-align: left;
}

.end-score-head {
	font-family: var(--font-body);
	font-size: 14px;
	font-weight: 600;
	color: var(--ink);
}

.end-score-sub {
	font-family: var(--font-body);
	font-size: 12px;
	color: var(--muted-2);
	margin-top: 2px;
}

/* share block */
.end-share-head {
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.14em;
	text-transform: uppercase;
	color: var(--muted);
	margin-bottom: 10px;
}

/* next puzzle countdown */
/* practice: prev / next archive navigation under the box art */
.end-practice-nav {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	margin-top: 16px;
}

.end-practice-nav-gap {
	flex: 1;
}

.end-practice-nav-btn {
	flex: none;
	padding: 9px 16px;
	font-family: var(--font-mono);
	font-size: 11px;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--ink);
	background: transparent;
	border: 1px solid var(--tile);
	border-radius: var(--radius);
	cursor: pointer;
	transition: background 140ms ease, border-color 140ms ease;
}

.end-practice-nav-btn:hover {
	background: #f4eee2;
	border-color: var(--muted);
}

.end-next {
	display: flex;
	align-items: baseline;
	justify-content: center;
	gap: 8px;
	padding-top: 16px;
}

/* mobile-first: the countdown lives in the main column; the art-column copy is
   desktop-only (the breakpoint below swaps them) */
.end-next--art {
	display: none;
}

.end-next-label {
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: var(--muted);
}

.end-next-time {
	font-family: var(--font-mono);
	font-size: 16px;
	color: var(--ink);
	font-variant-numeric: tabular-nums;
}

/* DESKTOP: two columns, centred. Art fixed-width left, content flexes right. */
@media (min-width: 880px) {

	.end-body {
		padding: 56px 32px;
	}

	.end-shell {
		max-width: 980px;
		flex-direction: row;
		align-items: flex-start;
		gap: 48px;
	}

	.end-art-col {
		flex: none;
		width: 440px;
	}

	/* desktop: countdown moves under the box art, not in the content column */
	.end-next--art {
		display: flex;
		margin-top: 20px;
	}

	.end-next--main {
		display: none;
	}

	.end-main-col {
		flex: 1;
		min-width: 0;
		gap: 18px;
	}

	/* left-align the editorial title block on the wider layout */
	.end-title {
		text-align: left;
	}

	.end-name {
		font-size: 38px;
		letter-spacing: -0.015em;
	}

	.end-meta {
		font-size: 14px;
		margin-top: 7px;
	}

	.end-score {
		justify-content: flex-start;
		padding: 18px 20px;
	}

	.end-score-value {
		font-size: 48px;
	}

}

</style>
